import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://travel-boost.com";

// SEO Keyword sets for different languages
const SEO_KEYWORDS = {
  he: "Travel Boost, תכנון טיול, מחשבון תקציב טיול, המלצות לחו״ל, מסלולי טיול, מדריכי נסיעות, טיסות זולות, יעד לטיול הבא, ביטוח נסיעות, eSIM לחו״ל, השכרת רכב, אטרקציות, חופשה, נופש",
  en: "Travel Boost, Travel Planner, Trip Budget Calculator, World Travel Guides, Holiday Destinations, Itinerary Planner, Cheap Flights, Travel Insurance, eSIM, Car Rental",
  es: "Travel Boost, Planificador de Viajes, Calculadora de Presupuesto, Guías de Viaje, Destinos de Vacaciones, Vuelos Baratos, Seguro de Viaje"
};

interface PageSEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  keywords?: string;
  lang?: "he" | "en" | "es";
}

/**
 * PageSEO Component
 * 
 * Dynamically updates document head with SEO meta tags.
 * Uses travel-boost.com as the canonical domain (no www).
 * Injects high-volume keywords for better search visibility.
 * 
 * Usage:
 * <PageSEO 
 *   title="Page Title" 
 *   description="Page description for search engines"
 *   keywords="custom, keywords, here"
 * />
 */
export const PageSEO = ({
  title,
  description,
  image,
  type = "website",
  noindex = false,
  publishedTime,
  modifiedTime,
  author,
  keywords,
  lang = "he",
}: PageSEOProps) => {
  const location = useLocation();
  
  // Clean path: remove trailing slash for consistency
  const cleanPath = location.pathname === "/" 
    ? "" 
    : location.pathname.replace(/\/$/, "");
  
  const canonicalUrl = `${BASE_URL}${cleanPath}`;
  
  const fullTitle = title 
    ? `${title} | Travel Boost` 
    : "Travel Boost | מתכננים חופשה? הכלים החכמים למטייל";
  
  const metaDescription = description || 
    "Travel Boost: תכנון טיול חכם - מחשבון תקציב, מדריכי יעדים מקיפים, טיסות זולות, ביטוח נסיעות וחבילות eSIM. כל מה שצריך לחופשה מושלמת במקום אחד.";
  
  const ogImage = image || `${BASE_URL}/og-image.png`;
  
  // Merge custom keywords with base keywords for the language
  const metaKeywords = keywords 
    ? `${keywords}, ${SEO_KEYWORDS[lang]}` 
    : SEO_KEYWORDS[lang];

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tag
    const updateMetaTag = (
      selector: string, 
      attribute: string, 
      content: string
    ) => {
      let element = document.querySelector(selector) as HTMLMetaElement | null;
      if (element) {
        element.setAttribute("content", content);
      } else {
        element = document.createElement("meta");
        const [attrName, attrValue] = selector
          .replace("meta[", "")
          .replace("]", "")
          .split("=");
        element.setAttribute(attrName, attrValue.replace(/"/g, ""));
        element.setAttribute("content", content);
        document.head.appendChild(element);
      }
    };

    // Helper to update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (element) {
        element.setAttribute("href", href);
      } else {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        element.setAttribute("href", href);
        document.head.appendChild(element);
      }
    };

    // Core meta tags
    updateMetaTag('meta[name="description"]', "content", metaDescription);
    updateMetaTag('meta[name="keywords"]', "content", metaKeywords);
    updateMetaTag('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    
    // Canonical URL - always without trailing slash
    updateLinkTag("canonical", canonicalUrl);

    // Open Graph
    updateMetaTag('meta[property="og:title"]', "content", fullTitle);
    updateMetaTag('meta[property="og:description"]', "content", metaDescription);
    updateMetaTag('meta[property="og:url"]', "content", canonicalUrl);
    updateMetaTag('meta[property="og:image"]', "content", ogImage);
    updateMetaTag('meta[property="og:type"]', "content", type);

    // Twitter
    updateMetaTag('meta[name="twitter:title"]', "content", fullTitle);
    updateMetaTag('meta[name="twitter:description"]', "content", metaDescription);
    updateMetaTag('meta[name="twitter:url"]', "content", canonicalUrl);
    updateMetaTag('meta[name="twitter:image"]', "content", ogImage);

    // Article-specific meta (for blog posts)
    if (type === "article") {
      if (publishedTime) {
        updateMetaTag('meta[property="article:published_time"]', "content", publishedTime);
      }
      if (modifiedTime) {
        updateMetaTag('meta[property="article:modified_time"]', "content", modifiedTime);
      }
      if (author) {
        updateMetaTag('meta[property="article:author"]', "content", author);
      }
    }

    // Cleanup function to reset to defaults when component unmounts
    return () => {
      document.title = "Travel Boost | מתכננים חופשה? הכלים החכמים למטייל";
    };
  }, [fullTitle, metaDescription, metaKeywords, canonicalUrl, ogImage, type, noindex, publishedTime, modifiedTime, author]);

  return null;
};

export default PageSEO;
